import React, { useRef, useEffect, useState } from 'react';
import { ApplicationView } from '@aha-app/mvc';
import {
  loadValidateAIResponse,
  loadWhiteboard,
} from 'javascripts/async_modules';
import type Whiteboard from '@aha-app/whiteboard';
import { type validateAIResponse } from '@aha-app/whiteboard';
import { type MessageProps } from './MessageContent';
import ContentError from './ContentError';

let WhiteboardComponent: typeof Whiteboard | null = null;
let runValidateAIResponse: typeof validateAIResponse | null = null;

const loadWhiteboardCode = async () => {
  runValidateAIResponse = await loadValidateAIResponse();
  WhiteboardComponent = await loadWhiteboard();
};

const WhiteboardContent = ({ body }: MessageProps) => {
  let whiteboard: any;
  const [retry, setRetry] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keep checking to see if the whiteboard code is loaded yet.
  useEffect(() => {
    if (!WhiteboardComponent || !runValidateAIResponse) {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
      }
      timeoutRef.current = setInterval(() => setRetry(retry + 1), 50); // This will refresh the data at regularIntervals of refreshTime
    }
    return () => {
      if (timeoutRef.current) clearInterval(timeoutRef.current);
    };
  }, [retry]);

  if (!WhiteboardComponent || !runValidateAIResponse) {
    loadWhiteboardCode();
    return <div>Loading</div>;
  }

  try {
    whiteboard = JSON.parse(body);
    if (!whiteboard.objects || !whiteboard.version)
      return <ContentError message='Invalid whiteboard content' />;
  } catch (e) {
    console.error(e);
    return <ContentError message='Invalid whiteboard content' />;
  }

  // Validate the objects in the whiteboard before using them.
  const newObjects = {};
  for (const id in whiteboard.objects) {
    const object = whiteboard.objects[id];
    const result = runValidateAIResponse(object);
    if (result.success && result.data) {
      newObjects[result.data.id as string] = result.data;
    } else {
      console.error(result.errors);
      console.debug(object);
    }
  }
  whiteboard.objects = newObjects;

  return (
    // @ts-expect-error missing lots of props, but we don't need them for this view
    <WhiteboardComponent
      key={Math.random()}
      readOnly
      isFormField={false}
      allowAnnotate={false}
      allowUpdate={false}
      initialVersion={0}
      initialData={whiteboard}
      isEmbeddedPreview
    />
  );
};

export default ApplicationView(WhiteboardContent);
